import { Dealer } from 'domains/Dealer';
import React from 'react';
import { Panel } from 'components/AdForm/components/Panel/Panel';
import { PanelHeading } from 'components/AdForm/components/PanelHeading/PanelHeading';
import { ButtonLink } from 'components/Toolkit/Button/ButtonLink';
import { useDealerContactDetails } from 'components/DealerPage/components/DealerContactDetails/DealerContactDetails.hook';
import { Link } from 'components/Toolkit/Button/Link';
import {
  DealerChevron,
  ListItemTitle,
  SDealerContactLocation,
  SDealerContactLocationContainer,
} from 'components/DealerPage/components/DealerContactDetails/DealerContactDetails';
import { StarRatingGroup } from 'components/PageWrappers/AdDetails/components/GoogleReviews/StarRatingGroup/StarRatingGroup';
import * as Styled from 'components/DealerPage/components/ContactPage/ContactPage.styled';
import { DealerHours } from 'components/DealerPage/components/DealerHours/DealerHours';

interface OurStockPageProps {
  dealer: Dealer;
  onReviewClick: () => void;
  cdnUrl: string;
  domain: string;
}

function ContactPage({ dealer, onReviewClick }: OurStockPageProps) {
  const {
    dealerPhone,
    setLocationOpen,
    isLocationOpen,
    isHoursOpen,
    setHoursOpen,
    handleShowPhoneNumber,
    isSubmitting,
    setShowEmail,
    showEmail,
  } = useDealerContactDetails(dealer);
  return (
    <Styled.Main>
      <Styled.ContactWrapper>
        <Panel>
          <PanelHeading>Get In Touch</PanelHeading>
          <div className="row set-width-100">
            <div className="columns small-12 medium-6">
              <Styled.ButtonShowNumber
                ofType="PRIMARY"
                data-testid={
                  dealerPhone ? 'call-phone-number' : 'view-phone-number'
                }
                href={dealerPhone ? `tel:${dealerPhone}` : undefined}
                onClick={handleShowPhoneNumber}
              >
                {dealerPhone && !isSubmitting
                  ? `Call ${dealerPhone}`
                  : isSubmitting
                  ? 'Loading'
                  : 'Show Phone Number'}
              </Styled.ButtonShowNumber>

              {showEmail ? (
                <Styled.ButtonShowEmail
                  ofType={'SECONDARY'}
                  href={`mailto:${dealer.email}`}
                >
                  {dealer.email}
                </Styled.ButtonShowEmail>
              ) : (
                <Styled.ButtonShowEmail
                  ofType={'SECONDARY'}
                  onClick={(e) => {
                    e.preventDefault();
                    setShowEmail(!showEmail);
                  }}
                >
                  {showEmail ? dealer.email : 'Show Email'}
                </Styled.ButtonShowEmail>
              )}
            </div>
            <div className="columns small-12 medium-6">
              {dealer.rating && (
                <Styled.GoogleReviewsWrapper>
                  <span>Google Reviews</span>
                  <Styled.GoogleReviewsScore onClick={onReviewClick}>
                    <StarRatingGroup
                      rating={dealer.rating}
                      numberOfStars={5}
                      multipleStars={false}
                      starWidth={'16'}
                      starHeight={'16'}
                    />
                    <Styled.GoogleReviewDivider />
                    {dealer.ratingTotal} reviews
                  </Styled.GoogleReviewsScore>
                </Styled.GoogleReviewsWrapper>
              )}
              <DealerHours
                dealer={dealer}
                onToggleHours={() => setHoursOpen(!isHoursOpen)}
                isHoursOpen={isHoursOpen}
              />
              <SDealerContactLocationContainer>
                <SDealerContactLocation
                  onClick={() => setLocationOpen(!isLocationOpen)}
                >
                  <ListItemTitle isBold={false}>{'Location'}</ListItemTitle>
                  <ListItemTitle isBold={true}>
                    {dealer.address}
                    <DealerChevron
                      $isMenuOpen={isLocationOpen}
                      height={16}
                      width={16}
                      $isDisabled={false}
                    />
                  </ListItemTitle>
                </SDealerContactLocation>
                {isLocationOpen && (
                  <Styled.SDealerContactLocationGoogleMapsLinkWrapper>
                    <Link target={'_blank'} href={dealer.googleMapsLink}>
                      {dealer.fullAddress}
                    </Link>
                  </Styled.SDealerContactLocationGoogleMapsLinkWrapper>
                )}
              </SDealerContactLocationContainer>
            </div>
            {dealer.googleMapPhoto && dealer.googleMapsLink && (
              <Styled.GoogleMapsWrapper>
                <Styled.GoogleMaps
                  className="columns small-12"
                  backgroundImage={dealer.googleMapPhoto}
                >
                  <ButtonLink
                    target={'_blank'}
                    href={dealer.googleMapsLink}
                    ofType={'SECONDARY'}
                  >
                    Get Directions
                  </ButtonLink>
                </Styled.GoogleMaps>
              </Styled.GoogleMapsWrapper>
            )}
          </div>
        </Panel>

        {dealer.website && dealer.description && (
          <Panel>
            <PanelHeading>About Us</PanelHeading>
            <Styled.SSimpleShowMoreInline
              visibleText={dealer.description}
              showMoreText={'Read More'}
              readMoreDataTracking={'about-us-read-more'}
            >
              <Styled.ReadMore>
                <Styled.SToolkitLink
                  href={dealer.website ? dealer.website : '#'}
                  target="_blank"
                  ofType="SECONDARY"
                  fontSize="M14"
                  data-tracking={'view-website'}
                  rel="nofollow"
                >
                  View Website
                  <Styled.SOpenOutlineIc width="16" height="16" />
                </Styled.SToolkitLink>
              </Styled.ReadMore>
            </Styled.SSimpleShowMoreInline>
          </Panel>
        )}
      </Styled.ContactWrapper>
    </Styled.Main>
  );
}

export default ContactPage;
